import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calculator } from "lucide-react";

/** Quick count for a collapsed tree level: every product that goes into this
 *  building / floor / area with its total quantity. Read-only. */
export default function GroupCountDialog({ group, columns, qmap, onClose }) {
  if (!group) return null;
  const totals = {};
  for (const r of group.rooms) {
    const q = qmap[r.key] || {};
    for (const ck of Object.keys(q)) totals[ck] = (totals[ck] || 0) + q[ck];
  }
  const rows = columns.filter((c) => (totals[c.key] || 0) > 0);
  const byScope = rows.reduce((acc, c) => { (acc[c.scope || ""] = acc[c.scope || ""] || []).push(c); return acc; }, {});
  const grand = rows.reduce((s, c) => s + totals[c.key], 0);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="group-count-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calculator className="w-4 h-4 text-blue-700" /> {group.label}
          </DialogTitle>
          <DialogDescription className="text-[11px]" data-testid="group-count-summary">
            {group.rooms.length} room(s) · {rows.length} product(s) · {grand} unit(s). What goes in here, for a quick count.
          </DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500" data-testid="group-count-empty">Nothing scheduled here.</p>
        ) : (
          <table className="w-full text-xs" data-testid="group-count-rows">
            <thead className="text-[10px] uppercase text-gray-500">
              <tr><th className="text-left p-1">Product</th><th className="text-left p-1 w-24">Short</th>
                <th className="text-right p-1 w-16">Qty</th></tr>
            </thead>
            <tbody>
              {Object.keys(byScope).sort().map((scope) => (
                <React.Fragment key={scope}>
                  {Object.keys(byScope).length > 1 && (
                    <tr className="bg-gray-50"><td colSpan={3} className="p-1 text-[10px] font-semibold uppercase text-gray-600">{scope || "No scope"}</td></tr>
                  )}
                  {byScope[scope].map((c) => (
                    <tr key={c.key} className="border-t border-gray-100" data-testid={`group-count-${c.key}`}>
                      <td className="p-1">{c.desc || c.dtext || c.code || c.short}</td>
                      <td className="p-1 font-mono text-gray-600">{c.short || c.code}</td>
                      <td className="p-1 text-right tabular-nums font-semibold">{totals[c.key]}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot><tr className="border-t border-gray-300 font-semibold">
              <td className="p-1" colSpan={2}>Total</td>
              <td className="p-1 text-right tabular-nums" data-testid="group-count-total">{grand}</td>
            </tr></tfoot>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
